import { FORMATIONS, positionPenalty, type Position } from "@legacy-manager/engine";
import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from "react";

import { distribuir, sugeridosPara } from "../../game/posicao";
import type { LivePlayerRow, PlayerRow } from "../../game/types";
import { sectorOf } from "../../game/views";
import { useT } from "../../i18n";
import { PositionChip, StrengthBadge, strengthColor } from "../../ui/atoms";
import { AvatarDoJogador } from "../../ui/avatar/Avatar";
import { EscalacaoNoCampo, faixaDeCondicao, nomeCurto } from "../EscalacaoNoCampo";
import type { LadoDaPartida } from "./tipos";

/**
 * A substituição com a cara da tática.
 *
 * O campo em perspectiva da tela de Tática, com o time como está agora, e ao
 * lado a lista de quem pode entrar. Toca-se em quem sai, a lista se ordena por
 * quem cobre a vaga, toca-se em quem entra, e a troca sai na hora. É a mesma
 * lista de quem entra que o jogo já usava, só que agora ao lado do campo, e
 * não de outra lista.
 */

/** Ordem das posições em campo, do gol ao ataque, para ordenar o banco pela vaga. */
const ORDEM_POSICAO: Record<string, number> = {
  GOL: 0,
  ZAG: 1,
  LD: 2,
  LE: 2,
  VOL: 3,
  MC: 4,
  MEI: 5,
  PD: 6,
  PE: 6,
  ATA: 7,
};

/** Quão bem um reserva cobre a vaga de quem está saindo. Menor é melhor. */
export function distanciaDaVaga(reserva: string, vaga: string): number {
  const a = ORDEM_POSICAO[reserva] ?? 4;
  const b = ORDEM_POSICAO[vaga] ?? 4;
  if ((reserva === "GOL") !== (vaga === "GOL")) return 99;
  return Math.abs(a - b);
}

/** O que a ficha do campo precisa saber de um jogador da partida. */
function linhaDoJogador(jogador: LivePlayerRow): PlayerRow {
  return {
    id: jogador.id,
    name: jogador.name,
    pos: jogador.pos,
    sector: sectorOf(jogador.pos),
    age: 0,
    nationality: jogador.nationality,
    strength: jogador.strength,
    highlights: [],
    morale: 60,
    condition: jogador.condition,
    value: 0,
    salary: 0,
    shirt: jogador.shirt,
    injuryRounds: 0,
    suspensionRounds: 0,
    goals: 0,
    assists: 0,
    apps: 0,
    yellow: 0,
    rating: 0,
    starter: true,
    benched: false,
    playingPos: jogador.slot,
    contractEndsSeason: 0,
    transferListed: false,
    star: false,
  };
}

export interface TrocaDaPartida {
  /** O meu lado, como o campo desenha. */
  lado: LadoDaPartida;
  emCampo: LivePlayerRow[];
  banco: LivePlayerRow[];
  trocasRestantes: number;
  /** Janela fechada, ou espectador: dá para olhar, não para trocar. */
  bloqueado: boolean;
  /** O que dizer no alto, além dos contadores: a janela e o prazo, no online. */
  rotulo?: ReactNode;
  aoTrocar: (saiId: string, entraId: string) => void;
  /**
   * O ajuste tático, só no intervalo: trocar a formação (os onze são
   * redistribuídos pelas vagas novas) e trocar dois de posição (toca num,
   * toca no outro). Fora do intervalo fica ausente, e a tela só substitui.
   */
  ajuste?: { aoAjustar: (formation: string, starters: string[]) => void };
  /** Os botões do alto: voltar ao jogo, continuar. */
  acoes: ReactNode;
}

/**
 * Os mesmos jogadores nas vagas de outra formação.
 *
 * Quem é da posição fica nela, e o resto vai para a vaga mais perto da dele,
 * pesando o time inteiro e não vaga a vaga: vaga a vaga, a zaga era
 * preenchida primeiro e o atacante que sobrava ia parar lá. Goleiro no gol.
 * Com dez em campo, a última vaga fica vazia.
 */
export function redistribuir(emCampo: LivePlayerRow[], slots: string[]): string[] {
  const vagas = slots.slice(0, emCampo.length);
  return distribuir(vagas, emCampo, (j) => j.pos, (j, vaga) => j.strength * positionPenalty(j.pos as Position, vaga as Position))
    .filter((j): j is LivePlayerRow => j !== null)
    .map((j) => j.id);
}

export function SubstituicaoNoCampo({ troca, minuto }: { troca: TrocaDaPartida; minuto: number }): ReactNode {
  const t = useT();
  const [vaga, definirVaga] = useState<number | null>(null);
  const [confirmacao, definirConfirmacao] = useState<string | null>(null);

  const porId = useMemo(() => {
    const mapa = new Map<string, PlayerRow>();
    for (const jogador of [...troca.emCampo, ...troca.banco]) mapa.set(jogador.id, linhaDoJogador(jogador));
    return mapa;
  }, [troca.emCampo, troca.banco]);

  const starters = troca.lado.emCampo.map((v) => (v.sentOff ? "" : v.jogador.id));
  const saindo = vaga === null ? null : troca.emCampo.find((j) => j.id === starters[vaga]) ?? null;
  const vagaPos = vaga === null ? null : (troca.lado.slots[vaga] ?? null);
  const ajuste = troca.ajuste ?? null;
  const formacoes = useMemo(() => {
    const nomes = Object.keys(FORMATIONS);
    return nomes.includes(troca.lado.formation) ? nomes : [troca.lado.formation, ...nomes];
  }, [troca.lado.formation]);

  function mudarFormacao(nome: string): void {
    const formacao = FORMATIONS[nome];
    if (!ajuste || !formacao || nome === troca.lado.formation) return;
    ajuste.aoAjustar(nome, redistribuir(troca.emCampo, formacao.slots));
    definirVaga(null);
    definirConfirmacao(t("partida.ajustado", { formacao: nome }));
  }

  function escolher(indice: number): void {
    // No intervalo, tocar em dois em campo troca os dois de posição.
    if (ajuste && vaga !== null && vaga !== indice && starters[vaga] && starters[indice]) {
      const novos = starters.filter(Boolean);
      const a = novos.indexOf(starters[vaga] as string);
      const b = novos.indexOf(starters[indice] as string);
      if (a >= 0 && b >= 0) {
        [novos[a], novos[b]] = [novos[b] as string, novos[a] as string];
        ajuste.aoAjustar(troca.lado.formation, novos);
        const nomeA = porId.get(starters[vaga] as string)?.name ?? "";
        const nomeB = porId.get(starters[indice] as string)?.name ?? "";
        definirConfirmacao(t("partida.trocouPosicao", { a: nomeCurto(nomeA), b: nomeCurto(nomeB) }));
      }
      definirVaga(null);
      return;
    }
    definirVaga((atual) => (atual === indice ? null : indice));
  }

  // A confirmação some sozinha.
  useEffect(() => {
    if (!confirmacao) return;
    const id = window.setTimeout(() => definirConfirmacao(null), 3200);
    return () => window.clearTimeout(id);
  }, [confirmacao]);

  const semTrocas = troca.bloqueado || troca.trocasRestantes <= 0;
  const banco = useMemo(() => {
    const lista = [...troca.banco];
    if (vagaPos) {
      lista.sort(
        (a, b) => distanciaDaVaga(a.pos, vagaPos) - distanciaDaVaga(b.pos, vagaPos) || b.strength - a.strength,
      );
    } else lista.sort((a, b) => b.strength - a.strength);
    return lista;
  }, [troca.banco, vagaPos]);

  // A sugestão que pulsa: com uma vaga pega, os reservas daquela posição (a
  // vizinha, se não houver nenhum) e, no intervalo, quem é dela e joga em outra.
  const sugeridos = useMemo(() => {
    const ids = new Set<string>();
    if (!vagaPos || vaga === null) return ids;
    if (!semTrocas) for (const reserva of sugeridosPara(vagaPos, troca.banco, (j) => j.pos)) ids.add(reserva.id);
    if (ajuste) {
      const deslocados = troca.emCampo.filter((j) => j.id !== starters[vaga] && j.pos !== j.slot);
      for (const jogador of sugeridosPara(vagaPos, deslocados, (j) => j.pos)) ids.add(jogador.id);
    }
    return ids;
  }, [vagaPos, vaga, semTrocas, ajuste, troca.banco, troca.emCampo, starters]);

  function trocar(entra: LivePlayerRow): void {
    if (!saindo || semTrocas) return;
    troca.aoTrocar(saindo.id, entra.id);
    definirConfirmacao(
      t("partida.trocado", {
        sai: `${saindo.shirt === null ? "" : String(saindo.shirt) + " "}${saindo.name}`,
        entra: `${entra.shirt === null ? "" : String(entra.shirt) + " "}${entra.name}`,
      }),
    );
    definirVaga(null);
  }

  return (
    <section className="pf-troca" aria-label={t("partida.substituicao")}>
      <div className="pf-troca-campo card">
        <div className="pf-troca-topo">
          <div className="pf-contagem">
            <span>
              <span className="label-muted">{t("partida.trocas")}</span>{" "}
              <b className="num">{troca.trocasRestantes}</b>
            </span>
            <span className="label-muted num">{t("aoVivo.minuto", { minuto })}</span>
            {troca.rotulo && <span className="label-muted">{troca.rotulo}</span>}
            {ajuste && (
              <label className="pf-formacao">
                <span className="label-muted">{t("tatica.formacao")}</span>
                <select
                  id="pf-formacao"
                  className="num"
                  value={troca.lado.formation}
                  disabled={troca.bloqueado}
                  onChange={(evento) => mudarFormacao(evento.target.value)}
                >
                  {formacoes.map((nome) => (
                    <option key={nome} value={nome}>
                      {nome}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
          <div className="pf-controles">{troca.acoes}</div>
        </div>
        <div className="pf-troca-palco tatica">
          <EscalacaoNoCampo
            formacao={troca.lado.formation}
            slots={troca.lado.slots}
            pontos={troca.lado.custom}
            starters={starters}
            porId={porId}
            rendimentoDe={(id) => porId.get(id)?.strength ?? 0}
            aoEscolher={escolher}
            classeDa={(indice) => (indice === vaga ? "pego" : sugeridos.has(starters[indice] ?? "") ? "sugerido" : "")}
            rotuloDa={(indice) => {
              const jogador = porId.get(starters[indice] ?? "");
              return jogador ? `${jogador.name} · ${troca.lado.slots[indice] ?? ""}` : (troca.lado.slots[indice] ?? "");
            }}
          />
        </div>
      </div>

      <div className="pf-troca-lista card">
        <div className="pf-sai">
          <span className="label-muted">{t("partida.sai")}</span>
          {saindo ? (
            <div className="pf-sai-card">
              <span
                className="campo-foto pf-sai-foto"
                data-cond={faixaDeCondicao(saindo.condition)}
                style={{ "--cond": saindo.condition } as CSSProperties}
              >
                <span className="campo-retrato">
                  <AvatarDoJogador playerId={saindo.id} nacionalidade={saindo.nationality} rosto />
                </span>
              </span>
              <div className="pf-sai-nome">
                <b className="corta">
                  {saindo.shirt !== null && <span className="num">{saindo.shirt} </span>}
                  {saindo.name}
                </b>
                <div className="pf-sai-linha">
                  <PositionChip pos={saindo.pos} />
                  <span className="num muted">{t("partida.condicao", { condicao: saindo.condition })}</span>
                </div>
                <div className="pf-cond" data-cond={faixaDeCondicao(saindo.condition)}>
                  <i style={{ width: `${String(saindo.condition)}%` }} />
                </div>
              </div>
              <StrengthBadge value={saindo.strength} />
            </div>
          ) : (
            <div className="pf-sai-card" data-vazio="" />
          )}
        </div>

        <div className="pf-entra-topo">
          <span className="label-muted">{t("partida.entra")}</span>
          <span className="muted num">{semTrocas ? t("partida.semTrocas") : t("partida.noBanco", { n: banco.length })}</span>
        </div>
        <div className="scroll">
          <div className="pf-entra">
            {banco.map((reserva) => {
              const delta = saindo ? reserva.strength - saindo.strength : 0;
              return (
                <button
                  key={reserva.id}
                  type="button"
                  className={`pf-entra-linha${sugeridos.has(reserva.id) ? " sugerido" : ""}`}
                  disabled={!saindo || semTrocas}
                  onClick={() => trocar(reserva)}
                >
                  <PositionChip pos={reserva.pos} />
                  <span className="pf-entra-nome">
                    <b className="corta">{nomeCurto(reserva.name)}</b>
                    <small className="num">{reserva.shirt ?? ""}</small>
                  </span>
                  <span className="pf-cond" data-cond={faixaDeCondicao(reserva.condition)}>
                    <i style={{ width: `${String(reserva.condition)}%` }} />
                  </span>
                  <span className="pf-forca num" style={{ background: strengthColor(reserva.strength) }}>
                    {reserva.strength}
                  </span>
                  <span className={`pf-delta num ${delta > 0 ? "mais" : delta < 0 ? "menos" : ""}`}>
                    {saindo ? (delta > 0 ? `+${String(delta)}` : String(delta)) : ""}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        {confirmacao && (
          <div className="pf-confirmacao" role="status">
            {confirmacao}
          </div>
        )}
      </div>
    </section>
  );
}
